import { showToast } from '../../engine/toast';
import { setTabBadge, resetTabBadges } from '../../engine/tab-badges';
import { fetchBuildings, fetchNotifications, fetchRoster, fetchTech, type GameNotification } from './notifications-api';
import { t, tOr } from '../../engine/i18n/i18n';
import { formatNotification } from './notification-format';
import { alertTabActive, isBattleResult, showBattleAlert } from '../../engine/battle-alert';

/**
 * Background module (not a GameTab — no mount(root), no tab). Started once
 * from the app entry point, independent of whichever tab is active.
 *
 * One responsibility: poll the player's own buildings/roster/tech every
 * POLL_INTERVAL_MS and toast when a timer that was mid-flight on the PREVIOUS
 * poll has resolved by THIS poll (server-side lazy finalization already
 * happened — we only detect the transition, never finalize anything
 * ourselves).
 *
 * It used to also paint a fixed "Online" / "Reconnecting…" pill in the top
 * right. That was removed: it occupied prime screen space permanently to
 * report a condition that is almost always fine, and a transient blip during
 * a reconnect read as a fault the player could do nothing about.
 */

export const POLL_INTERVAL_MS = 15_000;

interface Snapshot {
  buildings: Map<string, boolean>; // type -> constructing
  roster: Map<string, boolean>; // unitType -> training
  tech: boolean | null; // researching; null until the first poll lands
}

function emptySnapshot(): Snapshot {
  return { buildings: new Map(), roster: new Map(), tech: null };
}

export function initNotifications(): () => void {
  let disposed = false;
  let inFlight = false;
  let hasBaseline = false;
  const snapshot = emptySnapshot();
  /** Persisted notification ids already toasted/seen this session — unlike the timer-completion `hasBaseline` guard, a persisted notification SHOULD toast the very first time it's seen (that's the whole point: it survives reload/offline). */
  const seenNotificationIds = new Set<string>();
  let hasNotificationBaseline = false;

  async function poll(): Promise<void> {
    if (inFlight || disposed) return;
    inFlight = true;
    try {
      const [buildings, roster, tech, feed] = await Promise.all([
        fetchBuildings(),
        fetchRoster(),
        fetchTech(),
        fetchNotifications(),
      ]);
      if (disposed) return;

      if (hasBaseline) {
        for (const b of buildings) {
          const wasConstructing = snapshot.buildings.get(b.type);
          if (wasConstructing === true && !b.constructing) {
            showToast(t('notifications.constructionDone', { name: tOr(`term.building.${b.type}`, b.type) }), 'success');
          }
        }
        for (const r of roster) {
          const wasTraining = snapshot.roster.get(r.unitType);
          if (wasTraining === true && !r.training) {
            showToast(t('notifications.trainingDone', { name: tOr(`term.unit.${r.unitType}`, r.unitType) }), 'success');
          }
        }
        if (snapshot.tech === true && !tech.researching) {
          showToast(t('notifications.researchDone', { level: tech.techLevel }), 'success');
        }
      }

      snapshot.buildings = new Map(buildings.map((b) => [b.type, b.constructing]));
      snapshot.roster = new Map(roster.map((r) => [r.unitType, r.training]));
      snapshot.tech = tech.researching;
      hasBaseline = true;

      const unread = feed.items.filter((n) => !n.readAt);
      const newOnes = unread.filter((n: GameNotification) => !seenNotificationIds.has(n.id));
      if (!hasNotificationBaseline && unread.length > 0) {
        // First poll after load/reload: summarize rather than replaying every
        // toast at once (which could be a whole march's worth of events).
        showToast(t(unread.length === 1 ? 'notifications.unread.one' : 'notifications.unread.other', { count: unread.length }), 'info');
      } else {
        for (const n of newOnes.slice(0, 3)) {
          // Over the map / colony a finished battle gets the richer, clickable summary card instead of a toast.
          if (isBattleResult(n) && alertTabActive() && showBattleAlert(n)) continue;
          const { message, toastType } = formatNotification(n);
          showToast(message, toastType);
        }
        if (newOnes.length > 3) showToast(t('notifications.more', { count: newOnes.length - 3 }), 'info');
      }
      for (const n of feed.items) seenNotificationIds.add(n.id);
      hasNotificationBaseline = true;
      setTabBadge('battle', feed.unreadCount);
    } catch (err) {
      // Leave the snapshot/baseline untouched on failure — a transient
      // network blip must not re-arm the first-poll guard (which would
      // silently swallow a real completion) nor toast a false transition.
      console.error('[notifications] poll failed', err);
    } finally {
      inFlight = false;
    }
  }

  void poll();
  const intervalId = setInterval(() => {
    void poll();
  }, POLL_INTERVAL_MS);

  return function teardown(): void {
    disposed = true;
    clearInterval(intervalId);
    resetTabBadges();
  };
}
